'use client';

import { useEffect } from 'react';
import type { CoreEvents as CoreEventsType } from '@fanduel/cpe-um-bundle/core-events/library';

let initialized: boolean = false;
let initializationPromise: Promise<void> | null = null;

let coreEventsManager: typeof CoreEventsType | null = null;

/**
 * Awaits Core Events + unified modules init so `logEvent` is wired (not a no-op).
 * Safe to call from async handlers that fire before effects have finished initializing.
 * Rejects if init fails (after logging and resetting internal state so a later call can retry).
 */
export const ensureCoreEventsInitialized = async (): Promise<void> => {
    if (initialized) {
        return;
    }

    if (initializationPromise) {
        await initializationPromise;
        return;
    }

    initializationPromise = (async () => {
        try {
            coreEventsManager = (await import('@fanduel/cpe-um-bundle/core-events/library')).CoreEvents;

            const { initializeUnifiedModules } = await import('@/logic/unified-modules');
            await initializeUnifiedModules();

            initialized = true;
        } catch (error) {
            console.error('[ensureCoreEventsInitialized] Error initializing Core Events', error);
            coreEventsManager = null;
            initialized = false;
            initializationPromise = null;
            throw error;
        }
    })();

    await initializationPromise;
};

export const useCoreEvents = () => {
    const logEvent: typeof CoreEventsType.logEvent = (event) => {
        coreEventsManager?.logEvent(event);
    };

    useEffect(() => {
        ensureCoreEventsInitialized().catch(() => {
            // Rejection already logged inside ensureCoreEventsInitialized; avoid unhandled rejection in the effect.
        });
    }, []);

    return {
        logEvent
    };
};
